import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Globe, MessageSquare, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import apiService from "../../../service/Api";
import AssignmentReminderDialog from "../../crew-management/components/AssignmentReminderDialog";
import CrewAssignmentDialog from "../../crew-management/components/CrewAssignmentDialog";
import ProducerAssignmentDialog from "../../crew-management/components/ProducerAssignmentDialog";

interface INotificationData {
  id: string;
  type: string;
  user: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  action: string;
  target?: string;
  timestamp: string;
  isRead: boolean;
  record_id: number;
  trigger_source: string;
  record_data: {
    type: string;
    stream_id: number;
    image?: string;
    name?: string;
  };
}

interface NotificationApiItem {
  id: number | string;
  database_table?: string;
  created_by_username?: string;
  trigger_source?: string;
  title?: string;
  description?: string;
  due_date_time?: string | null;
  created_at?: string;
  status?: string;
  object_id?: number;
  related_object_data?: {
    type?: string;
    stream_id?: number;
    image?: string;
    name?: string;
  };
}
interface NotificationRecordData {
  type?: string;
  stream_id?: number | string;
}

interface Notification {
  record_id?: number | string;
  action?: string;
  record_data?: NotificationRecordData;
}

interface NotificationsPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onBadgeClick: (event: React.MouseEvent<HTMLElement>) => void;
  children: (unreadCount: number) => React.ReactNode;
}

const tabCategories = [
  { label: "All", icon: <Globe size={16} /> },
  { label: "Unread", icon: <MessageSquare size={16} /> },
  { label: "Read", icon: <Check size={16} /> },
];

export default function NotificationsPopover({
  anchorEl,
  open,
  onClose,
  children,
}: NotificationsPopoverProps) {
  const [activeTab, setActiveTab] = useState("All");

  const [selectedNotification, setSelectedNotification] = useState<
    number | null
  >(null);
  const [notifications, setNotifications] = useState<INotificationData[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showAssignmentReminderDialog, setShowAssignmentReminderDialog] =
    useState<boolean>(false);
  const [showProducerAssignmentDialog, setShowProducerAssignmentDialog] =
    useState<boolean>(false);
  const [showCrewAssignmentDialog, setShowCrewAssignmentDialog] =
    useState<boolean>(false);
  const [selectedStream, setSelectedStream] = useState<number | null>(null);

  const isPureBookingNotification = (item: INotificationData) => {
    return item.action.toLowerCase().startsWith("new booking #");
  };

  const extractBookingId = (text?: string): number | null => {
    if (!text) return null;

    const match = text.match(/booking\s*#(\d+)/i);
    return match ? Number(match[1]) : null;
  };

  const fetchDeceasedImage = useCallback(
    async (notification: INotificationData) => {
      const bookingId = extractBookingId(notification.target);
      if (!bookingId) return;
      try {
        const res = await apiService.get<{
          deceased?: Array<{ person_image?: string }>;
        }>(`/api/bookings/booking/${bookingId}/`);
        const image = res.data?.deceased?.[0]?.person_image;
        if (image) {
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id
                ? { ...n, record_data: { ...n.record_data, image } }
                : n,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch deceased image:", err);
      }
    },
    [],
  );

  const fetchVenueImage = useCallback(
    async (notification: INotificationData) => {
      if (!notification.record_id) return;
      try {
        const res = await apiService.get<{
          files?: Array<{ file?: string; is_cover?: boolean }>;
        }>(`/api/venues/venue/${notification.record_id}/`);
        const files = res.data?.files ?? [];
        const cover = files.find((f) => f.is_cover);
        const image = cover?.file || files[0]?.file;
        if (image) {
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id
                ? { ...n, record_data: { ...n.record_data, image } }
                : n,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch venue image:", err);
      }
    },
    [],
  );

  // FETCH FIRST PAGE
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await apiService.get<{
          results?: NotificationApiItem[];
          next?: string | null;
        }>("/api/notifications/notification/");

        const raw = res.data.results || [];

        const mapped = raw
          .map((item) => ({
            id: String(item.id),
            type: item.database_table || "general",
            trigger_source: item.trigger_source || "",
            user: {
              name: item.created_by_username || "Unknown User",
              avatar: "",
            },
            action: item.title || "Notification",
            target: item.description || "",
            timestamp: new Date(item.created_at || Date.now()).toLocaleString(),
            isRead: item.status === "read",
            record_id: item.object_id ?? 0,
            record_data: {
              type: item.related_object_data?.type || "unknown",
              stream_id: item.related_object_data?.stream_id ?? 0,
              image: item.related_object_data?.image || "",
              name: item.related_object_data?.name || "",
            },
          }))
          .filter((n) => !isPureBookingNotification(n));

        setNotifications(mapped);

        mapped.forEach((n) => {
          if (n.record_data?.type === "stream") fetchDeceasedImage(n);
          else if (n.trigger_source === "venue.venue") fetchVenueImage(n);
        });

        setNextPageUrl(res.data.next || null);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    fetchNotifications();
  }, [fetchDeceasedImage, fetchVenueImage]);

  const loadMoreNotifications = async () => {
    if (!nextPageUrl) return;

    setLoadingMore(true);

    try {
      const res = await apiService.get<{
        results?: NotificationApiItem[];
        next?: string | null;
      }>(nextPageUrl);

      const raw = res.data.results || [];

      const mapped = raw.map((item) => ({
        id: String(item.id),
        type: item.database_table || "general",
        trigger_source: item.trigger_source || "",
        user: {
          name: item.created_by_username || "Unknown User",
          avatar: "",
        },
        action: item.title || "Notification",
        target: item.description || "",
        timestamp: new Date(item.created_at || Date.now()).toLocaleString(),
        isRead: item.status === "read",
        record_id: item.object_id ?? 0,
        record_data: {
          type: item.related_object_data?.type || "unknown",
          stream_id: item.related_object_data?.stream_id ?? 0,
          image: item.related_object_data?.image || "",
          name: item.related_object_data?.name || "",
        },
      }));

      setNotifications((prev) => [...prev, ...mapped]);

      mapped.forEach((n) => {
        if (n.record_data?.type === "stream") fetchDeceasedImage(n);
        else if (n.trigger_source === "venue.venue") fetchVenueImage(n);
      });

      setNextPageUrl(res.data.next || null);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    }

    setLoadingMore(false);
  };

  // TAB FILTERS
  const filteredNotifications = useMemo(() => {
    if (activeTab === "Unread") return notifications.filter((n) => !n.isRead);
    if (activeTab === "Read") return notifications.filter((n) => n.isRead);
    return notifications;
  }, [activeTab, notifications]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications],
  );

  const handleToggleReadStatus = async (id: string) => {
    try {
      await apiService.patch(`/api/notifications/notification/${id}/`, {
        status: "read",
      });
    } catch (err) {
      console.error("Failed to update:", err);
    }

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: !n.isRead } : n)),
    );
  };

  const handleRemoveNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const getNotificationLink = (n: Notification) => {
    if (!n.record_id) return undefined;

    const action = n.action?.toLowerCase() || "";

    if (action.includes("sponsorship stream")) {
      return `/sponsorships/view/${n.record_id}`;
    }

    // Stream
    if (action.includes("stream")) {
      if (n.record_data?.type === "call_sheet") {
        return `/stream-management/view/${n.record_data?.stream_id}`;
      }
      return `/stream-management/view/${n.record_id}`;
    }

    // Booking Quote Request
    if (action.includes("quote")) {
      return `/request-for-quote/${n.record_id}`;
    }

    // Booking (non-quote)
    if (action.includes("booking")) {
      return `/bookings/${n.record_id}`;
    }

    // Venue
    if (action.includes("venue")) {
      return `/venues/view/${n.record_id}`;
    }

    return undefined;
  };

  return (
    <>
      {children(unreadCount)}

      {/* POPUP */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={onClose}>
          <Box>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card
                    sx={{
                      width: 420,
                      borderRadius: 4,
                      boxShadow: 6,
                      bgcolor: "background.paper",
                    }}
                  >
                    <CardContent>
                      {/* HEADER */}
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" fontWeight="bold">
                          Notifications
                        </Typography>
                        <IconButton onClick={onClose}>
                          <X size={20} />
                        </IconButton>
                      </Box>

                      {/* TABS */}
                      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        {tabCategories.map((tab) => (
                          <Chip
                            key={tab.label}
                            label={tab.label}
                            icon={tab.icon}
                            clickable
                            color={
                              activeTab === tab.label ? "primary" : "default"
                            }
                            onClick={() => setActiveTab(tab.label)}
                          />
                        ))}
                      </Box>

                      {/* NOTIFICATIONS */}
                      <Box
                        sx={{
                          maxHeight: 400,
                          zIndex: 10,
                          overflowY: "auto",
                          mt: 2,
                        }}
                      >
                        <AnimatePresence mode="popLayout">
                          {filteredNotifications.length > 0 ? (
                            filteredNotifications.map((n) => {
                              return (
                                <motion.div
                                  key={n.id}
                                  style={{ zIndex: 10 }}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                >
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 3,
                                      mb: 2,
                                      zIndex: 10,
                                      bgcolor: n.isRead
                                        ? "grey.100"
                                        : "#f0f4f8",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                      {n.record_data?.image && (
                                        <Avatar
                                          src={n.record_data.image}
                                          variant="rounded"
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            flexShrink: 0,
                                            borderRadius: "50%",
                                          }}
                                        />
                                      )}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          component="a"
                                          href={getNotificationLink(n)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          fontWeight="bold"
                                          sx={{
                                            color: "primary.main",
                                            textDecoration: "none",
                                            "&:hover": {
                                              textDecoration: "underline",
                                            },
                                            cursor: "pointer",
                                          }}
                                        >
                                          {n.action}
                                        </Typography>

                                        {n.target && (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              mt: 0.5,
                                              whiteSpace: "pre-line",
                                            }}
                                          >
                                            {n.target}
                                          </Typography>
                                        )}

                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ mt: 1, display: "block" }}
                                        >
                                          {n.timestamp}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mt: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {n.record_id &&
                                        n.record_data?.type === "stream" &&
                                        !n.action
                                          .toLowerCase()
                                          .includes("quote") && (
                                          <Chip
                                            component="a"
                                            clickable
                                            label="View Booking"
                                            href={`/bookings/${extractBookingId(n.target)}`}
                                            sx={{
                                              bgcolor: "#DCFCE7",
                                              color: "#15803D",
                                              fontWeight: 600,
                                            }}
                                          />
                                        )}

                                      {/* VIEW STREAM */}
                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes("new stream created") ||
                                        n.action
                                          .toLowerCase()
                                          .includes(
                                            "new producer assignment",
                                          ) ||
                                        n.action
                                          .toLowerCase()
                                          .includes("new stream scheduled") ||
                                        n.action
                                          .toLowerCase()
                                          .includes("gig accepted") ||
                                        n.action
                                          .toLowerCase()
                                          .includes("gig declined")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="View Stream"
                                          href={`/stream-management/view/${n.record_data?.type === "call_sheet" ? n.record_data?.stream_id : n.record_id}`}
                                          sx={{
                                            bgcolor: "#E0F2FE",
                                            color: "#0369A1",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {/* ASSIGN PRODUCER */}
                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes("new stream created") ||
                                        n.action
                                          .toLowerCase()
                                          .includes("new stream scheduled")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="Assign Producer"
                                          onClick={() => {
                                            setSelectedStream(n.record_id);
                                            setShowProducerAssignmentDialog(
                                              true,
                                            );
                                          }}
                                          sx={{
                                            bgcolor: "#DCFCE7",
                                            color: "#15803D",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {/* ASSIGN CREW */}
                                      {n.record_id &&
                                      n.action
                                        .toLowerCase()
                                        .includes("new producer assignment") ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="Assign Crew"
                                          onClick={() => {
                                            setSelectedStream(
                                              n.record_data?.stream_id,
                                            );
                                            setShowCrewAssignmentDialog(true);
                                          }}
                                          sx={{
                                            bgcolor: "#DCFCE7",
                                            color: "#15803D",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes("gig accepted") ||
                                        n.action
                                          .toLowerCase()
                                          .includes("gig declined")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="Assign Crew"
                                          onClick={() => {
                                            setSelectedStream(n.record_id);
                                            setShowCrewAssignmentDialog(true);
                                          }}
                                          sx={{
                                            bgcolor: "#DCFCE7",
                                            color: "#15803D",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {/* REMIND LATER */}
                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes("new stream created") ||
                                        n.action
                                          .toLowerCase()
                                          .includes(
                                            "new producer assignment",
                                          ) ||
                                        n.action
                                          .toLowerCase()
                                          .includes("new stream scheduled")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="Remind Later"
                                          onClick={() => {
                                            setSelectedNotification(
                                              parseInt(n.id),
                                            );
                                            setSelectedStream(
                                              n.record_data?.type ===
                                                "call_sheet"
                                                ? n.record_data?.stream_id
                                                : n.record_id,
                                            );
                                            setShowAssignmentReminderDialog(
                                              true,
                                            );
                                          }}
                                          sx={{
                                            bgcolor: "#F79E1B20",
                                            color: "#F79E1B",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {/* VIEW VENUE */}
                                      {n.trigger_source === "venue.venue" && (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="View Venue"
                                          href={`/venues/view/${n.record_id}`}
                                          sx={{
                                            bgcolor: "#FEE2E2",
                                            color: "#B91C1C",
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}

                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes("sponsorship stream") ||
                                        n.action
                                          .toLowerCase()
                                          .includes("sponsorship stream")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="View Sponsorship Application"
                                          href={`/sponsorships/view/${n.record_id}`}
                                          sx={{
                                            bgcolor: "#DCFCE7",
                                            color: "#15803D",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      {/* PREVIEW QUOTATION DETAILS */}
                                      {n.record_id &&
                                      (n.action
                                        .toLowerCase()
                                        .includes(
                                          "new booking quote request",
                                        ) ||
                                        n.action
                                          .toLowerCase()
                                          .includes("quotation ready")) ? (
                                        <Chip
                                          component="a"
                                          clickable
                                          label="Preview Quotation Details"
                                          href={`/request-for-quote/${n.record_id}`}
                                          sx={{
                                            bgcolor: "#DCFCE7",
                                            color: "#15803D",
                                            fontWeight: 600,
                                          }}
                                        />
                                      ) : null}

                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        {/* MARK READ */}
                                        <Tooltip title="Mark as read">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleToggleReadStatus(n.id);
                                            }}
                                          >
                                            <Check size={18} />
                                          </IconButton>
                                        </Tooltip>

                                        {/* DELETE */}
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveNotification(n.id);
                                          }}
                                        >
                                          <X size={18} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Box>
                                </motion.div>
                              );
                            })
                          ) : (
                            <Box
                              textAlign="center"
                              py={4}
                              color="text.secondary"
                            >
                              No notifications found.
                            </Box>
                          )}
                        </AnimatePresence>

                        {/* LOAD MORE BUTTON */}
                        {nextPageUrl && (
                          <Box textAlign="center" mt={2} mb={2}>
                            <Button
                              onClick={loadMoreNotifications}
                              disabled={loadingMore}
                              variant="outlined"
                            >
                              {loadingMore ? (
                                <CircularProgress size={18} />
                              ) : (
                                "Load More"
                              )}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* DIALOGS */}
      <AssignmentReminderDialog
        notification={selectedNotification}
        setShowAssignmentReminderDialog={setShowAssignmentReminderDialog}
        showAssignmentReminderDialog={showAssignmentReminderDialog}
        selectedStream={selectedStream}
      />

      <ProducerAssignmentDialog
        setShowProducerAssignmentDialog={setShowProducerAssignmentDialog}
        showProducerAssignmentDialog={showProducerAssignmentDialog}
        selectedStream={selectedStream}
      />

      <CrewAssignmentDialog
        setShowCrewAssignmentDialog={setShowCrewAssignmentDialog}
        showCrewAssignmentDialog={showCrewAssignmentDialog}
        selectedStream={selectedStream}
      />
    </>
  );
}
